// Canonical IDs + identity for the ACTIVE sales-demo tenant.
//
// Historically this file hard-coded a single tenant ("Demo Arena"). It now
// projects whichever client is active (see ./clients) onto the same export
// surface the rest of the fixtures consume, so switching clients re-skins every
// screen from one source of truth. The named exports below are unchanged so the
// ../data fixtures keep importing exactly what they always did.

import { ACTIVE } from './clients'
import type { DemoClient, DemoZoneMeta } from './clients/types'

export type { DemoZoneMeta }

// ── Active-tenant identity / branding ────────────────────────────────────────
export const DEMO_CLIENT: DemoClient = ACTIVE
export const DEMO_CLIENT_ID = ACTIVE.id
export const DEMO_CLIENT_NAME = ACTIVE.name
export const DEMO_TIMEZONE = ACTIVE.timezone
export const DEMO_ROLE = ACTIVE.user.role
export const DEMO_USER_EMAIL = ACTIVE.user.email
export const DEMO_USER_NAME = ACTIVE.user.name
export const DEMO_ACCENT = ACTIVE.accent

// ── Staff demo mode (QA / screenshots, NOT sales) ────────────────────────────
//
// `VITE_DEMO_STAFF=1` seeds a PARKIFY-INTERNAL operator instead of a tenant one.
// The difference that matters is `clientId: null` — App.tsx gates every
// PARKIFY_STAFF_ONLY screen (Support, Customers, Integrations, Parkify Admin,
// platform analytics, kill switches) on `user.clientId != null`, so a
// client-scoped demo operator cannot reach them even by typing the URL.
//
// OFF BY DEFAULT, and it must stay that way: the deployed *-demo sites are what
// a rep drives live on a prospect call, and the internal support inbox is not a
// surface to put in front of a prospect. Turning this on is a local/QA act —
// `npm run dev:demo:staff` — not a deploy configuration. Nothing in
// scripts/deploy* passes it.
export const DEMO_STAFF = import.meta.env.VITE_DEMO_STAFF === '1'

// `VITE_DEMO_ZONE_SCOPE=<zoneId>[,<zoneId>…]` reports the demo operator as
// pinned to those zones in /me's canonical `scope` (LOC-04), so the console's
// zone-scoped location controls can be reviewed and screenshotted. Scope ONLY:
// the demo fixtures are not scope-filtered, so lists still show every zone.
// A local/QA act like VITE_DEMO_STAFF, ignored in staff mode (staff are never
// pinned), and passed by no deploy script. A function, not a module-level
// value: this module ships in the console's entry chunk even in production
// builds, and a top-level computation there is bytes in the budgeted initial
// payload that only demo builds ever use.
export function demoZoneScope(): string[] | null {
  const ids = String(import.meta.env.VITE_DEMO_ZONE_SCOPE ?? '')
    .split(',').map(id => id.trim()).filter(Boolean)
  return ids.length > 0 ? ids : null
}

// A Parkify staff operator. Deliberately not a real teammate: demo builds get
// screenshotted into PRs and decks, and a colleague's name and address on a
// fixture inbox reads as real correspondence.
//
// Role `support` by default (the Support inbox fixtures). `VITE_DEMO_STAFF_ROLE=owner`
// seeds a Parkify owner instead, the role that holds every console control, so
// the staff-capability browser pass can open every gated screen and write
// control as staff (docs/DEMO-MODE-TESTING.md). Local/QA only, like
// VITE_DEMO_STAFF itself.
export const DEMO_STAFF_USER = {
  // example.com (RFC 2606): unmistakably fictitious in a screenshot.
  email: 'demo.staff@example.com',
  name: 'Demo Staff',
  role: (import.meta.env.VITE_DEMO_STAFF_ROLE === 'owner' ? 'owner' : 'support') as 'owner' | 'support',
}

// ── Zones ────────────────────────────────────────────────────────────────────
// Master zone table for the active tenant.
export const DEMO_ZONES: DemoZoneMeta[] = ACTIVE.zones

// Role slots — index 0 = primary, 1 = overflow, 2 = premium. Fixtures join on
// these so the same narrative (event spike on primary+premium, overflow lot, …)
// works for any tenant regardless of the literal zone ids.
export const ZONE_MAIN = ACTIVE.zones[0].id
export const ZONE_NORTH = ACTIVE.zones[1].id
export const ZONE_VIP = ACTIVE.zones[2].id

export const zoneById = (id: string): DemoZoneMeta | undefined =>
  DEMO_ZONES.find(z => z.id === id)

export const zoneName = (id: string): string =>
  zoneById(id)?.name ?? id

// The CUSTOMER-FACING code for a zone ("MD1") — the demo's analogue of the
// API's zoneDisplayName(). Distinct from zoneName(), which is the human lot
// name ("Main Deck"), and from the internal id ("za-main-deck") that a fixture
// must never hand a screen as a label.
export const zoneCode = (id: string): string =>
  zoneById(id)?.displayName ?? id

// ── Shared sample pools ──────────────────────────────────────────────────────
// Both demo tenants are Georgia operators, so one GA-heavy plate + email pool is
// scattered across sessions, disputes, permits and enforcement for realism.
export const DEMO_PLATES = [
  'GA7TRX204', 'GA4MVL881', 'FLKAP902', 'GA9XQ117', 'TNRBC540',
  'GA2WHL663', 'SCJTM228', 'GA8PND045', 'NCVRK719', 'GA6LZA390',
  'ALMQT156', 'GA3FED772', 'GA5KOP418', 'FLWYN633', 'GA1BST209',
]

export const DEMO_EMAILS = [
  'm.thompson@gmail.com', 'jchen.atl@outlook.com', 'driver8842@yahoo.com',
  's.patel@gmail.com', 'k.williams@icloud.com', 'rob.fitz@gmail.com',
  'a.nguyen@gmail.com', 'taylor.b@hotmail.com', 'devon.r@gmail.com',
  'priya.s@gmail.com', 'mike.dolan@gmail.com', 'l.garcia@outlook.com',
]
